import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "../lib/supabase";
import type { ProofMetricType, ProofDataMap } from "../types/core";
import { getProofCache, setProofCache } from "../lib/setterPerformanceCacheService";
import { toOrgStartISO, toOrgEndISO } from "../lib/timezone";

// =============================================================================
// USE SETTER PROOF HOOK
// =============================================================================
// PURPOSE: Lazy-fetch proof data for setter performance metrics.
// Only fetches when fetchProof() is called (not on mount).
//
// USAGE:
//   const { data, isLoading, error, fetchProof } = useSetterProof({
//     organizationId, setterIds, dateRange
//   });
//   // On metric click:
//   await fetchProof('engaged_response');
// =============================================================================

export interface UseSetterProofOptions {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string; // org timezone for date boundary conversion (default "UTC")
}

export interface UseSetterProofResult<T extends ProofMetricType = ProofMetricType> {
  data: ProofDataMap[T][] | null;
  isLoading: boolean;
  error: string | null;
  fetchProof: (metricType: T) => Promise<void>;
  clear: () => void;
}

export function useSetterProof<T extends ProofMetricType = ProofMetricType>(
  options: UseSetterProofOptions
): UseSetterProofResult<T> {
  const { organizationId, setterIds, dateRange, timezone } = options;

  const [data, setData] = useState<ProofDataMap[T][] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const fetchProof = useCallback(
    async (metricType: T) => {
      if (!organizationId || !supabase || setterIds.length === 0) return;
      if (!dateRange.start || !dateRange.end) return;

      // Abort previous request
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      // Stale-while-revalidate: show cached proof data instantly
      const cached = organizationId
        ? getProofCache<ProofDataMap[T][]>(organizationId, dateRange, metricType)
        : null;
      if (cached) {
        setData(cached);
      } else {
        setIsLoading(true);
        setData(null);
      }
      setError(null);

      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc("get_setter_proof", {
          p_organization_id: organizationId,
          p_setter_ids: setterIds,
          p_start_date: toOrgStartISO(dateRange.start, timezone),
          p_end_date: toOrgEndISO(dateRange.end, timezone),
          p_metric_type: metricType,
        });

        if (rpcError) throw rpcError;

        // RPC returns JSONB — Supabase client already parses it
        const result = (rpcData || []) as ProofDataMap[T][];
        setData(result);
        if (organizationId) {
          setProofCache(organizationId, dateRange, metricType, result);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (!cached) {
          console.warn("Failed to fetch proof data:", err);
          setError(err instanceof Error ? err.message : "Failed to fetch proof data");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, setterIds, dateRange.start, dateRange.end]
  );

  const clear = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  // Abort in-flight requests on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { data, isLoading, error, fetchProof, clear };
}
